'use client';

import Image from '@/components/Image';
import { useEffect, useRef } from 'react';

interface CutFitSectionData {
  img: string;
  title: string;
  content: string;
  btn_link: string;
  btn_name?: string;
}

interface CutFitSectionProps {
  data: CutFitSectionData | null;
  isMobile: boolean;
  onDownloadClick?: (btn_link?: string) => void;
  onExposure?: (
    mkt_name: string,
    pits_index: number,
    pits_jump_type: string,
    pits_jump_content: string,
  ) => void;
}

export default function CutFitSection({
  data,
  isMobile,
  onDownloadClick,
  onExposure,
}: CutFitSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasTrackedExposure = useRef(false);

  // 曝光埋点 - 模拟 Vue 中的 v-exposure-pro 指令
  useEffect(() => {
    // 对 data 做了空值保护，避免在 hooks 里访问空对象属性。，
    // 这个读取会在“计算依赖数组”的阶段就直接抛错，导致 React 渲染失败-重试-再失败，从而看起来像进入了死循环并伴随一堆报错。
    if (!onExposure || hasTrackedExposure.current || !data?.btn_link) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedExposure.current) {
            hasTrackedExposure.current = true;
            onExposure('Frame Cutouts', 0, '4', data.btn_link);
          }
        });
      },
      {
        threshold: 0.5, // 当50%可见时触发
      },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [data?.btn_link, onExposure]);

  const handleDownload = () => {
    const url = data?.btn_link;
    if (url) {
      // 先调用埋点
      onDownloadClick?.(url);
      // 再打开下载链接
      window.open(url, '_blank');
    }
  };

  // 无数据时不渲染内容
  if (!data) {
    return null;
  }

  return (
    <div
      ref={sectionRef}
      className={`cut-fit-section ${isMobile ? 'py-[32px] px-[14px] pt-0' : 'py-[2.97vw] pb-[5vw] px-[10.42vw] flex justify-between items-center'}`}
    >
      <Image
        className={`block ${isMobile ? 'w-full mb-[20px] aspect-[694/280]' : 'w-[34.375vw] aspect-[720/240]'}`}
        src={data.img}
        alt={data.title}
        width={isMobile ? 694 : 720}
        height={isMobile ? 280 : 240}
      />
      <div className={`${isMobile ? '' : 'w-[37.5vw]'}`}>
        <div
          className={`${
            isMobile
              ? 'text-[16px] leading-[20px] text-center'
              : 'text-[max(1.67vw,20px)] leading-[max(39px,20px)] text-left '
          }`}
          style={{ fontFamily: 'var(--font-bricolage-bold)' }}
          dangerouslySetInnerHTML={{ __html: data.title }}
        />
        <div
          className={`${
            isMobile
              ? 'mt-[9px] mb-[20px] text-[13px] leading-[20px] text-center'
              : 'mt-[0.94vw] mb-[1.25vw] text-[max(0.83vw,14px)] leading-[max(1.25vw,14px)] text-left'
          }`}
          dangerouslySetInnerHTML={{ __html: data.content }}
        />
        <button
          className={`flex justify-center items-center ${
            isMobile
              ? 'w-[254px] h-[36px] mx-auto bg-[#282828] shadow-[0_10px_19px_0_rgba(137,144,154,0.1)] rounded-[18px] text-[14px] text-white'
              : 'w-[13.54vw] h-[2.5vw] min-w-[186px] min-h-[34px] bg-[#282828] shadow-[0_6px_12px_0_rgba(137,144,154,0.1)] rounded-[24px] text-[max(0.83vw,14px)] text-white'
          }`}
          onClick={handleDownload}
        >
          {data.btn_name || 'DOWNLOAD TEMPLATE'}
        </button>
      </div>
    </div>
  );
}
